import {
  describeElementTypeOptions,
  isElementTypeValidForFieldType,
  resolveElementTypeName
} from "../../form-element-type-options";
import { isEngineProcessAction } from "../../managed-task-screen";
import type {
  AssignScreenCmd,
  ConditionEntry,
  ConnectCmd,
  DslCommand,
  InteractionCmd,
  ParamBoCmd,
  PathCmd,
  PhaseAssignCmd,
  PhaseCmd,
  ScreenFieldCmd,
  ScreenFormCmd,
  ScreenSectionCmd
} from "./workflow-dsl-parser";
import {
  compositionDeclaredAlias,
  compositionReferencedAliases,
  isScreenCompositionCommand,
  isScreenLoadEvent,
  type ScreenCompositionCommand
} from "./workflow-dsl-parser";

// ── Types ──────────────────────────────────────────────────────────────────

export interface ValidationError {
  line: number;
  rule: string;
  message: string;
  severity: "error" | "warning";
}

// KOA-18448 — the PLATFORM spelling is `isGreaterOrEqual` / `isLessOrEqual` (what the workflow
// generator switches on and what the parser now emits). This module used to know only its own
// `isGreaterThanOrEqual` spelling; both are accepted so a script parsed either way is judged.
const LOWER_BOUND_OPS: ReadonlySet<string> = new Set([
  ">",
  ">=",
  "isGreaterThan",
  "isGreaterOrEqual",
  "isGreaterThanOrEqual"
]);
const UPPER_BOUND_OPS: ReadonlySet<string> = new Set(["<", "<=", "isLessThan", "isLessOrEqual", "isLessThanOrEqual"]);

/**
 * Detect a "between two bounds" range mistakenly written with OR instead of AND
 * — which is ALWAYS TRUE. `tutar >= 10000 OR tutar < 50001` matches every value
 * (anything not below the lower bound is caught by the upper, and vice-versa),
 * so on an exclusive gateway the band never actually filters and higher bands
 * are unreachable (KOA-17362 "satinalma": the 10K-50K band used OR, so 50K+
 * amounts matched it and never reached the 50K branch). Returns true ONLY for
 * the tautology shape: an OR of exactly two comparisons on the SAME field, one
 * lower-bound and one upper-bound, with lower value <= upper value. The legit
 * "outside a band" OR (`x < LO OR x > HI`, LO<HI) has lower value > upper value
 * and is NOT flagged. Exported for testing.
 */
export function isAlwaysTrueOrRange(conditions: readonly ConditionEntry[] | undefined, isOrGroup: boolean): boolean {
  if (!isOrGroup || !conditions || conditions.length !== 2) return false;
  const [a, b] = conditions;
  if (a.propertyName !== b.propertyName) return false;
  const an: number = typeof a.value === "number" ? a.value : Number(a.value);
  const bn: number = typeof b.value === "number" ? b.value : Number(b.value);
  if (!Number.isFinite(an) || !Number.isFinite(bn)) return false;
  let lowerVal: number;
  let upperVal: number;
  if (LOWER_BOUND_OPS.has(a.operator) && UPPER_BOUND_OPS.has(b.operator)) {
    lowerVal = an;
    upperVal = bn;
  } else if (UPPER_BOUND_OPS.has(a.operator) && LOWER_BOUND_OPS.has(b.operator)) {
    lowerVal = bn;
    upperVal = an;
  } else {
    return false;
  }
  return lowerVal <= upperVal;
}

interface NodeEntry {
  alias: string;
  kind: string;
  gatewayType?: "Exclusive" | "Inclusive" | "Parallel";
  gatewayRole?: "diverge" | "converge";
  hasAfter: boolean;
  hasBefore: boolean;
  afterRef?: string; // ref that THIS node comes after (ref is source of incoming to this)
  beforeRef?: string; // ref that THIS node comes before (ref is target of outgoing from this)
  line: number;
}

// ── Main validator ─────────────────────────────────────────────────────────

export function validateDslCommands(commands: DslCommand[]): ValidationError[] {
  const errors: ValidationError[] = [];

  // Phase 1: Build registries
  const nodes = new Map<string, NodeEntry>();
  const paths: PathCmd[] = [];
  const connects: ConnectCmd[] = [];
  const hasLint = commands.some((c) => c.kind === "LINT");
  const hasLayout = commands.some((c) => c.kind === "LAYOUT");

  for (const cmd of commands) {
    switch (cmd.kind) {
      case "TASK":
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "TASK",
          hasAfter: !!cmd.afterNodeId,
          hasBefore: !!cmd.beforeNodeId,
          afterRef: cmd.afterNodeId,
          beforeRef: cmd.beforeNodeId,
          line: cmd.line
        });
        break;
      case "SYSTEM":
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "SYSTEM",
          hasAfter: !!cmd.afterNodeId,
          hasBefore: !!cmd.beforeNodeId,
          afterRef: cmd.afterNodeId,
          beforeRef: cmd.beforeNodeId,
          line: cmd.line
        });
        break;
      case "GATEWAY":
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "GATEWAY",
          gatewayType: cmd.gatewayType,
          gatewayRole: cmd.role,
          hasAfter: !!cmd.afterNodeId,
          hasBefore: !!cmd.beforeNodeId,
          afterRef: cmd.afterNodeId,
          beforeRef: cmd.beforeNodeId,
          line: cmd.line
        });
        break;
      case "SUBPROCESS":
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "SUBPROCESS",
          hasAfter: !!cmd.afterNodeId,
          hasBefore: false,
          afterRef: cmd.afterNodeId,
          line: cmd.line
        });
        break;
      case "LINKED":
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "LINKED",
          hasAfter: !!cmd.afterNodeId,
          hasBefore: false,
          afterRef: cmd.afterNodeId,
          line: cmd.line
        });
        break;
      case "TIMER":
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "TIMER",
          hasAfter: !!cmd.afterNodeId,
          hasBefore: !!cmd.beforeNodeId,
          afterRef: cmd.afterNodeId,
          beforeRef: cmd.beforeNodeId,
          line: cmd.line
        });
        break;
      case "BOUNDARY":
        // Boundary event is attached to a host task via `on:`. The
        // attachment counts as implicit incoming to the boundary itself,
        // but we do NOT treat it as outgoing for the host — the host's
        // normal outgoing is separate (for non-interrupting boundaries
        // the host continues; for interrupting ones the host's normal
        // flow never fires but must still be modeled).
        nodes.set(cmd.alias, {
          alias: cmd.alias,
          kind: "BOUNDARY",
          hasAfter: true, // attached to host = implicit incoming
          hasBefore: false,
          // intentionally NO afterRef so host's outgoing check isn't
          // satisfied purely by this boundary attachment
          line: cmd.line
        });
        break;
      case "PATH":
        paths.push(cmd);
        break;
      case "CONNECT":
        connects.push(cmd);
        break;
    }
  }

  // Phase 2: Run validations
  validateForwardReferences(commands, errors);
  validateConnectionRefsDeclared(nodes, paths, connects, errors);
  validateStartEndEdgeDirection(nodes, paths, connects, errors);
  validatePathSourceIsGateway(nodes, paths, errors);
  validateGatewayMinPaths(nodes, paths, errors);
  validateExclusiveDefaults(nodes, paths, errors);
  validateExclusiveConditions(nodes, paths, errors);
  validateNoDuplicatePathTarget(paths, errors);
  validateOrRangeTautology(commands, errors);
  validateGatewayReachabilityFromInteractions(commands, nodes, paths, connects, errors);
  validateParallelPairs(nodes, errors);
  validateInclusivePairs(nodes, errors);
  validateParallelNoConditions(nodes, paths, errors);
  validateNodeConnectivity(nodes, paths, connects, errors);
  validateSingleOutgoingNonGateway(nodes, paths, connects, errors);
  validateScreenFieldConfig(commands, nodes, errors);
  validateScreenComposition(commands, nodes, errors);
  validateHappyPathPhases(commands, errors);
  validateMissingLintLayout(hasLint, hasLayout, errors);

  return errors;
}

/**
 * Aliases used in `after:` / `before:` / `on:` modifiers MUST be defined by
 * an EARLIER line in the same script. Forward references cause silent
 * execution failures — the node creation fails, then every downstream PATH/
 * CONNECT that targets it also fails, cascading into missing-connection
 * lint errors that look unrelated to the original mistake.
 */
function validateForwardReferences(commands: DslCommand[], errors: ValidationError[]): void {
  const defined = new Set<string>();
  for (const cmd of commands) {
    // Check each modifier that can hold an alias, BEFORE recording this
    // command's own alias (so self-references still fail).
    const checkRef = (ref: string | undefined, modifier: string): void => {
      if (!ref || !ref.startsWith("@")) return; // literal BPMN IDs are fine
      if (!defined.has(ref)) {
        errors.push({
          line: cmd.line,
          rule: "forward-reference",
          message: `Alias ${ref} used in \`${modifier}:\` is not defined yet. Move the creation of ${ref} BEFORE this line, or drop the \`${modifier}:\` modifier and wire the connection later with a CONNECT/PATH from that node. DSL is sequential — aliases must be defined on an earlier line.`,
          severity: "error"
        });
      }
    };

    // Collect refs used by this command
    switch (cmd.kind) {
      case "TASK":
      case "SYSTEM":
        checkRef(cmd.afterNodeId, "after");
        checkRef(cmd.beforeNodeId, "before");
        checkRef(cmd.laneId, "lane");
        break;
      case "GATEWAY":
        checkRef(cmd.afterNodeId, "after");
        checkRef(cmd.beforeNodeId, "before");
        break;
      case "SUBPROCESS":
      case "LINKED":
        checkRef(cmd.afterNodeId, "after");
        break;
      case "TIMER":
        checkRef(cmd.afterNodeId, "after");
        checkRef(cmd.beforeNodeId, "before");
        break;
      case "BOUNDARY":
        checkRef(cmd.nodeId, "on");
        break;
    }

    // Now record this command's alias so subsequent lines can reference it.
    if ("alias" in cmd && typeof cmd.alias === "string" && cmd.alias.startsWith("@")) {
      defined.add(cmd.alias);
    }
  }
}

// ── Individual rules ───────────────────────────────────────────────────────

/** Does a ref point at the Start event? (DSL reserved alias StartEvent_1, or any StartEvent id.) */
function refIsStart(ref: string | undefined): boolean {
  return !!ref && /startevent/i.test(ref);
}

/** Does a ref point at the End event? (DSL reserved alias EndEvent_1, or any EndEvent id.) */
function refIsEnd(ref: string | undefined): boolean {
  return !!ref && /endevent/i.test(ref);
}

/**
 * The Start event has NO incoming and the End event has NO outgoing — that's
 * fundamental BPMN. Reject the edges that violate it BEFORE the generic
 * connectivity check, so the model gets a precise message instead of being
 * nudged into a worse workaround. Weak models hit this constantly: to satisfy
 * "node X has no outgoing" they write `before:StartEvent_1` (a back-edge into
 * Start) or `after:EndEvent_1` (an edge out of End) instead of the correct
 * `CONNECT X -> EndEvent_1`.
 */
function validateStartEndEdgeDirection(
  nodes: Map<string, NodeEntry>,
  paths: PathCmd[],
  connects: ConnectCmd[],
  errors: ValidationError[]
): void {
  for (const [alias, node] of nodes) {
    // `before:StartEvent_1` → alias → Start (gives Start an incoming).
    if (refIsStart(node.beforeRef)) {
      errors.push({
        line: node.line,
        rule: "edge-into-start",
        message: `"${alias}" uses before:${node.beforeRef}, which would point INTO the Start event — Start has no incoming. To give "${alias}" an outgoing, connect it to the next real step or to EndEvent_1 (CONNECT ${alias} -> EndEvent_1), not back to Start.`,
        severity: "error"
      });
    }
    // `after:EndEvent_1` → End → alias (gives End an outgoing).
    if (refIsEnd(node.afterRef)) {
      errors.push({
        line: node.line,
        rule: "edge-out-of-end",
        message: `"${alias}" uses after:${node.afterRef}, which would point OUT of the End event — End has no outgoing. Place "${alias}" after a real step instead, and CONNECT the last step to EndEvent_1.`,
        severity: "error"
      });
    }
  }
  const checkEdge = (sourceId: string, targetId: string, line: number): void => {
    if (refIsStart(targetId)) {
      errors.push({
        line,
        rule: "edge-into-start",
        message: `Cannot connect INTO the Start event ("${targetId}") — Start has no incoming connection. Wire "${sourceId}" to the next real step or to EndEvent_1 instead.`,
        severity: "error"
      });
    }
    if (refIsEnd(sourceId)) {
      errors.push({
        line,
        rule: "edge-out-of-end",
        message: `Cannot connect OUT of the End event ("${sourceId}") — End has no outgoing connection. The End event is terminal.`,
        severity: "error"
      });
    }
  };
  for (const p of paths) checkEdge(p.sourceNodeId, p.targetNodeId, p.line);
  for (const c of connects) checkEdge(c.sourceNodeId, c.targetNodeId, c.line);
}

/** PATH source must reference a gateway alias. */
function validatePathSourceIsGateway(nodes: Map<string, NodeEntry>, paths: PathCmd[], errors: ValidationError[]): void {
  for (const path of paths) {
    const src = path.sourceNodeId;
    // Only validate aliases (@...) — literal BPMN IDs can't be checked statically
    if (!src.startsWith("@")) continue;
    const node = nodes.get(src);
    if (node && node.kind !== "GATEWAY") {
      errors.push({
        line: path.line,
        rule: "path-source-must-be-gateway",
        message: `PATH source "${src}" is a ${node.kind}, not a Gateway. Use CONNECT for task-to-task flows, or add a XGATEWAY after the task and PATH from there.`,
        severity: "error"
      });
    }
  }
}

/** Every gateway must have >= 2 outgoing PATHs. */
function validateGatewayMinPaths(nodes: Map<string, NodeEntry>, paths: PathCmd[], errors: ValidationError[]): void {
  for (const [alias, node] of nodes) {
    if (node.kind !== "GATEWAY") continue;
    if (node.gatewayRole === "converge") continue; // Join gateways don't need outgoing PATHs

    const outgoing = paths.filter((p) => p.sourceNodeId === alias);
    if (outgoing.length < 2) {
      errors.push({
        line: node.line,
        rule: "gateway-min-2-paths",
        message: `Gateway "${alias}" has ${outgoing.length} outgoing PATH(s) but needs >= 2. Add more PATH commands from this gateway.`,
        severity: "error"
      });
    }
  }
}

/** Exclusive gateways must have exactly 1 DEFAULT path. */
function validateExclusiveDefaults(nodes: Map<string, NodeEntry>, paths: PathCmd[], errors: ValidationError[]): void {
  for (const [alias, node] of nodes) {
    if (node.kind !== "GATEWAY") continue;
    if (node.gatewayType !== "Exclusive") continue;
    if (node.gatewayRole === "converge") continue;

    const outgoing = paths.filter((p) => p.sourceNodeId === alias);
    if (outgoing.length === 0) continue; // Already flagged by min-paths

    const defaults = outgoing.filter((p) => p.isDefault);
    if (defaults.length === 0) {
      errors.push({
        line: node.line,
        rule: "xgateway-needs-default",
        message: `Exclusive Gateway "${alias}" has no DEFAULT path. Exactly one outgoing PATH must be marked DEFAULT.`,
        severity: "error"
      });
    } else if (defaults.length > 1) {
      errors.push({
        line: defaults[1].line,
        rule: "xgateway-single-default",
        message: `Exclusive Gateway "${alias}" has ${defaults.length} DEFAULT paths — only 1 is allowed.`,
        severity: "error"
      });
    }
  }
}

/**
 * A "between two bounds" range written with OR is always true — flag it so the
 * agent uses AND (see isAlwaysTrueOrRange). Covers PATH ... WHEN ... and
 * CONDITION ... WHEN ... — both carry `conditions` + `isOrGroup`.
 */
function validateOrRangeTautology(commands: DslCommand[], errors: ValidationError[]): void {
  for (const cmd of commands) {
    if (cmd.kind !== "PATH" && cmd.kind !== "CONDITION") continue;
    if (!isAlwaysTrueOrRange(cmd.conditions, cmd.isOrGroup === true)) continue;
    const field: string = cmd.conditions?.[0]?.propertyName ?? "<field>";
    errors.push({
      line: cmd.line,
      rule: "or-range-always-true",
      message:
        `These two OR conditions on "${field}" together match EVERY value (always true) — a "between" range must use AND, not OR. ` +
        `On an exclusive gateway this band never filters, so higher bands become unreachable. ` +
        `Use \`WHEN ${field} >= <low> AND ${field} <= <high>\` (a value BETWEEN two bounds needs AND).`,
      severity: "error"
    });
  }
}

const OPERATOR_ALIASES: Readonly<Record<string, string>> = {
  "==": "isEqualTo",
  "!=": "isNotEqualTo",
  ">": "isGreaterThan",
  "<": "isLessThan",
  ">=": "isGreaterOrEqual",
  "<=": "isLessOrEqual",
  // This module's own historical spelling, kept as an alias so nothing that still writes it drops out.
  isGreaterThanOrEqual: "isGreaterOrEqual",
  isLessThanOrEqual: "isLessOrEqual",
  equals: "isEqualTo"
};

function canonicalOperator(op: string): string {
  return OPERATOR_ALIASES[op] ?? op;
}

/**
 * Evaluate ONE `WHEN` condition against the literal values a decision button
 * pins via `SET`. Returns `undefined` when the button does not pin that
 * parameter (or the comparison is not decidable on literals) — an unknown makes
 * NO reachability claim, which is what keeps this rule free of false positives.
 * Exported for testing.
 */
export function conditionVerdictUnderSetValues(
  condition: ConditionEntry,
  setValues: ReadonlyMap<string, string>
): boolean | undefined {
  const pinned: string | undefined = setValues.get(condition.propertyName.toLowerCase());
  if (pinned === undefined) return undefined;

  const operator: string = canonicalOperator(condition.operator);
  if (operator === "isEmpty") return pinned.trim().length === 0;
  if (operator === "isNotEmpty") return pinned.trim().length > 0;

  const left: number = Number(pinned);
  const right: number = typeof condition.value === "number" ? condition.value : Number(condition.value);
  const numeric: boolean = Number.isFinite(left) && Number.isFinite(right) && pinned.trim().length > 0;

  switch (operator) {
    case "isEqualTo":
      return numeric ? left === right : pinned.toLowerCase() === String(condition.value).toLowerCase();
    case "isNotEqualTo":
      return numeric ? left !== right : pinned.toLowerCase() !== String(condition.value).toLowerCase();
    case "isGreaterThan":
      return numeric ? left > right : undefined;
    case "isLessThan":
      return numeric ? left < right : undefined;
    case "isGreaterOrEqual":
      return numeric ? left >= right : undefined;
    case "isLessOrEqual":
      return numeric ? left <= right : undefined;
    default:
      return undefined;
  }
}

/** Whether a path's WHEN group is taken under the button's pinned values. */
function pathVerdictUnderSetValues(path: PathCmd, setValues: ReadonlyMap<string, string>): boolean | undefined {
  const conditions: readonly ConditionEntry[] = path.conditions ?? [];
  if (conditions.length === 0) return undefined;
  const isOr: boolean = path.isOrGroup === true;
  let sawUnknown: boolean = false;
  for (const condition of conditions) {
    const verdict: boolean | undefined = conditionVerdictUnderSetValues(condition, setValues);
    if (verdict === undefined) {
      sawUnknown = true;
      continue;
    }
    if (isOr && verdict) return true;
    if (!isOr && !verdict) return false;
  }
  if (sawUnknown) return undefined;
  return !isOr;
}

/**
 * A decision button's `SET` pins FIXED literals, so any gateway condition it
 * covers is already decided BEFORE the approver acts. When every button on the
 * task feeding an exclusive gateway evaluates to the SAME branch, the gateway's
 * other branches are DEAD — the diagram still LINTs clean and the approver still
 * sees both buttons, so nothing surfaces until someone approves a request and
 * watches it get rejected.
 *
 * Live incident (KOA-17362 smoke, 2026-07-28): the accounting task MAP_OUTPUT'd
 * both the decision AND the approved amount, so the over-broad "SET every
 * MAP_OUTPUT" reading forced `INTERACTION "Onayla" SET accountingApproved=true
 * approvedAmount=0`, while the reject path read
 * `WHEN accountingApproved == false OR approvedAmount <= 0`. Approving pinned
 * the amount to 0, matched the reject condition, and the approve branch could
 * never be taken.
 *
 * Only claims a collapse when EVERY button is fully decidable and each matches
 * AT MOST ONE non-default path; anything unknown or ambiguous is skipped.
 */
function validateGatewayReachabilityFromInteractions(
  commands: DslCommand[],
  nodes: Map<string, NodeEntry>,
  paths: PathCmd[],
  connects: ConnectCmd[],
  errors: ValidationError[]
): void {
  const buttonsByTask = new Map<string, InteractionCmd[]>();
  for (const cmd of commands) {
    if (cmd.kind !== "INTERACTION" || cmd.parameters.length === 0) continue;
    const list: InteractionCmd[] = buttonsByTask.get(cmd.taskId) ?? [];
    list.push(cmd);
    buttonsByTask.set(cmd.taskId, list);
  }
  if (buttonsByTask.size === 0) return;

  for (const [alias, node] of nodes) {
    if (node.kind !== "GATEWAY" || node.gatewayType !== "Exclusive" || node.gatewayRole === "converge") continue;

    const outgoing: PathCmd[] = paths.filter((p) => p.sourceNodeId === alias);
    if (outgoing.length < 2) continue;
    const fallback: PathCmd | undefined = outgoing.find((p) => p.isDefault);
    const nonDefault: PathCmd[] = outgoing.filter((p) => !p.isDefault);
    if (!fallback || nonDefault.length === 0) continue;

    // Reason only about a gateway with a SINGLE inbound step: with a second one
    // another token could still reach the branches these buttons never take.
    const incoming: string[] = [];
    if (node.afterRef !== undefined) incoming.push(node.afterRef);
    for (const [otherAlias, other] of nodes) {
      if (otherAlias !== alias && other.beforeRef === alias) incoming.push(otherAlias);
    }
    for (const connect of connects) {
      if (connect.targetNodeId === alias) incoming.push(connect.sourceNodeId);
    }
    for (const path of paths) {
      if (path.targetNodeId === alias) incoming.push(path.sourceNodeId);
    }
    const inboundSteps: string[] = Array.from(new Set(incoming));
    if (inboundSteps.length !== 1) continue;

    const buttons: InteractionCmd[] | undefined = buttonsByTask.get(inboundSteps[0]);
    if (!buttons || buttons.length < 2) continue;

    const landings = new Set<string>();
    let decidable: boolean = true;
    for (const button of buttons) {
      const setValues = new Map<string, string>();
      for (const assignment of button.parameters) {
        setValues.set(assignment.name.toLowerCase(), assignment.value);
      }

      const matched: PathCmd[] = [];
      for (const path of nonDefault) {
        const verdict: boolean | undefined = pathVerdictUnderSetValues(path, setValues);
        if (verdict === undefined) {
          decidable = false;
          break;
        }
        if (verdict) matched.push(path);
      }
      if (!decidable || matched.length > 1) {
        decidable = false;
        break;
      }
      landings.add(matched.length === 1 ? matched[0].targetNodeId : fallback.targetNodeId);
    }
    if (!decidable || landings.size !== 1) continue;

    const collapsed: string = Array.from(landings)[0];
    const dead: string[] = Array.from(new Set(outgoing.map((p) => p.targetNodeId))).filter((t) => t !== collapsed);
    if (dead.length === 0) continue;

    const labels: string = buttons.map((b) => `"${b.label}"`).join(", ");
    const pinned: string = Array.from(new Set(buttons.flatMap((b) => b.parameters.map((a) => a.name)))).join(", ");
    errors.push({
      line: node.line,
      rule: "gateway-unreachable-by-interactions",
      message:
        `Gateway "${alias}" can only ever take ONE branch: every decision button on "${inboundSteps[0]}" ` +
        `(${labels}) pins ${pinned} to fixed values that all route to "${collapsed}", so ${dead.map((t) => `"${t}"`).join(", ")} ` +
        `is unreachable. A button's SET is a FIXED literal, so it decides every gateway condition it covers BEFORE the approver acts. ` +
        `SET only the DECISION parameter the buttons own (a PARAM INTERNAL). A value the approver TYPES on the task form — an amount, ` +
        `a note, a corrected quantity — must be a \`PARAM BO … OUTPUT\` that the task MAP_OUTPUTs and that NO interaction SETs: ` +
        `leaving it out of the SET is exactly what makes the generated ResumeProcess read it from the approver's form field.`,
      severity: "error"
    });
  }
}

/** Non-default paths from exclusive/inclusive gateways must have WHEN conditions. */
function validateExclusiveConditions(nodes: Map<string, NodeEntry>, paths: PathCmd[], errors: ValidationError[]): void {
  for (const [alias, node] of nodes) {
    if (node.kind !== "GATEWAY") continue;
    if (node.gatewayType !== "Exclusive" && node.gatewayType !== "Inclusive") continue;
    if (node.gatewayRole === "converge") continue;

    const outgoing = paths.filter((p) => p.sourceNodeId === alias);
    for (const path of outgoing) {
      if (path.isDefault) continue;
      if (!path.conditions || path.conditions.length === 0) {
        errors.push({
          line: path.line,
          rule: "xgateway-path-needs-condition",
          message: `PATH from "${alias}" to "${path.targetNodeId}" is not DEFAULT but has no WHEN condition. Non-default paths on Exclusive/Inclusive Gateways must have conditions.`,
          severity: "error"
        });
      }
    }
  }
}

/**
 * No two PATHs from the same gateway may point to the same target. bpmn.js
 * + our post-processing silently merge duplicate (source, target) flows into
 * one, where the LATER path overwrites the earlier's name and DEFAULT/WHEN
 * state — a silent condition-loss bug. Force the LLM to combine conditions
 * on a single flow (compound WHEN via CONDITION ... OR ...).
 */
function validateNoDuplicatePathTarget(paths: PathCmd[], errors: ValidationError[]): void {
  const seen = new Map<string, PathCmd>();
  for (const p of paths) {
    const key = `${p.sourceNodeId}->${p.targetNodeId}`;
    const prior = seen.get(key);
    if (prior) {
      errors.push({
        line: p.line,
        rule: "path-duplicate-target",
        message:
          `Duplicate PATH: a flow from "${p.sourceNodeId}" to "${p.targetNodeId}" was already defined on line ${prior.line}. ` +
          `A gateway cannot have two separate outgoing flows to the same target — the DSL would silently merge them and one condition would be lost. ` +
          `Combine the conditions on a single path using CONDITION ... WHEN ... OR ..., or route one of the branches to a different target.`,
        severity: "error"
      });
    } else {
      seen.set(key, p);
    }
  }
}

/** Parallel gateways must come in pairs (diverge + converge). */
function validateParallelPairs(nodes: Map<string, NodeEntry>, errors: ValidationError[]): void {
  let divergeCount = 0;
  let convergeCount = 0;
  let lastDivergeLine = 0;

  for (const [, node] of nodes) {
    if (node.kind !== "GATEWAY" || node.gatewayType !== "Parallel") continue;
    if (node.gatewayRole === "diverge") {
      divergeCount++;
      lastDivergeLine = node.line;
    } else {
      convergeCount++;
    }
  }

  if (divergeCount !== convergeCount) {
    errors.push({
      line: lastDivergeLine || 0,
      rule: "parallel-must-pair",
      message: `Parallel Gateways must come in pairs: found ${divergeCount} PGATEWAY (diverge) and ${convergeCount} PJOIN (converge). They must match.`,
      severity: "error"
    });
  }
}

/** Inclusive gateways must come in pairs (diverge + converge). */
function validateInclusivePairs(nodes: Map<string, NodeEntry>, errors: ValidationError[]): void {
  let divergeCount = 0;
  let convergeCount = 0;
  let lastDivergeLine = 0;

  for (const [, node] of nodes) {
    if (node.kind !== "GATEWAY" || node.gatewayType !== "Inclusive") continue;
    if (node.gatewayRole === "diverge") {
      divergeCount++;
      lastDivergeLine = node.line;
    } else {
      convergeCount++;
    }
  }

  if (divergeCount > 0 && convergeCount === 0) {
    errors.push({
      line: lastDivergeLine,
      rule: "inclusive-must-pair",
      message: `Inclusive Gateways must come in pairs: found ${divergeCount} IGATEWAY (diverge) but 0 IJOIN (converge). Every IGATEWAY needs a matching IJOIN where branches merge back.`,
      severity: "error"
    });
  }
}

/** Parallel gateways must NOT have DEFAULT or conditions on their paths. */
function validateParallelNoConditions(
  nodes: Map<string, NodeEntry>,
  paths: PathCmd[],
  errors: ValidationError[]
): void {
  for (const [alias, node] of nodes) {
    if (node.kind !== "GATEWAY" || node.gatewayType !== "Parallel") continue;

    const outgoing = paths.filter((p) => p.sourceNodeId === alias);
    for (const path of outgoing) {
      if (path.isDefault) {
        errors.push({
          line: path.line,
          rule: "parallel-no-default",
          message: `Parallel Gateway "${alias}" must NOT have a DEFAULT path.`,
          severity: "error"
        });
      }
      if (path.conditions && path.conditions.length > 0) {
        errors.push({
          line: path.line,
          rule: "parallel-no-conditions",
          message: `Parallel Gateway "${alias}" must NOT have WHEN conditions on paths.`,
          severity: "error"
        });
      }
    }
  }
}

/**
 * Every created node MUST be connected (incoming AND outgoing).
 * Errors if a node will end up isolated — execution is blocked.
 */
function validateNodeConnectivity(
  nodes: Map<string, NodeEntry>,
  paths: PathCmd[],
  connects: ConnectCmd[],
  errors: ValidationError[]
): void {
  // Build sets of nodes referenced as source or target in PATH/CONNECT
  const referencedAsTarget = new Set<string>();
  const referencedAsSource = new Set<string>();

  for (const p of paths) {
    referencedAsTarget.add(p.targetNodeId);
    referencedAsSource.add(p.sourceNodeId);
  }
  for (const c of connects) {
    referencedAsTarget.add(c.targetNodeId);
    referencedAsSource.add(c.sourceNodeId);
  }

  // Build sets of nodes referenced as after:/before: by other nodes
  // If node B was created with `after:A`, then A gets outgoing flow to B (A is referencedAsAfter)
  // If node B was created with `before:A`, then A gets incoming flow from B (A is referencedAsBefore)
  const referencedAsAfter = new Set<string>();
  const referencedAsBefore = new Set<string>();
  for (const [, node] of nodes) {
    if (node.afterRef) referencedAsAfter.add(node.afterRef);
    if (node.beforeRef) referencedAsBefore.add(node.beforeRef);
  }

  for (const [alias, node] of nodes) {
    // Incoming sources:
    //   1. Node was created with `after:X` → X → this node (this node has incoming)
    //   2. Some other node was created with `before:this` → other → this node
    //   3. This node is target in some PATH or CONNECT
    const hasIncoming = node.hasAfter || referencedAsBefore.has(alias) || referencedAsTarget.has(alias);

    // Outgoing sources:
    //   1. Node was created with `before:X` → this node → X (this node has outgoing)
    //   2. Some other node was created with `after:this` → this → other
    //   3. This node is source in some PATH or CONNECT
    const hasOutgoing = node.hasBefore || referencedAsAfter.has(alias) || referencedAsSource.has(alias);

    // Completely isolated — highest priority error
    if (!hasIncoming && !hasOutgoing) {
      errors.push({
        line: node.line,
        rule: "node-will-be-isolated",
        message: `"${alias}" will be isolated: no after:/before: and not referenced in any PATH or CONNECT. Every node must connect to the flow.`,
        severity: "error"
      });
      continue;
    }

    // Missing incoming (except converge gateways which get it from PATHs targeting them)
    if (!hasIncoming) {
      errors.push({
        line: node.line,
        rule: "node-no-incoming",
        message: `"${alias}" has no incoming connection. Use after:<ref>, or make it a PATH/CONNECT target, or have another node created with before:${alias}.`,
        severity: "error"
      });
    }

    // Missing outgoing (tasks, systems, subprocesses, linked; gateways handled by min-2-paths rule)
    if (!hasOutgoing && node.kind !== "GATEWAY") {
      errors.push({
        line: node.line,
        rule: "node-no-outgoing",
        message: `"${alias}" has no outgoing connection. Use before:<ref>, or use it as a PATH/CONNECT source, or have another node created with after:${alias}.`,
        severity: "error"
      });
    }
  }
}

/** Levenshtein edit distance between two strings (pure, locale-independent). */
function editDistance(a: string, b: string): number {
  const m: number = a.length;
  const n: number = b.length;
  const prev: number[] = new Array<number>(n + 1);
  const curr: number[] = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost: number = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/**
 * Closest declared alias to `ref`, but only when genuinely close (a typo or a
 * shared stem like @kaliteBilgi vs @kaliteBildirim), not an arbitrary nearest
 * of two unrelated aliases. Returns "" when nothing is close enough.
 */
function nearestAlias(ref: string, declared: readonly string[]): string {
  let best: string = "";
  let bestDist: number = Infinity;
  for (const cand of declared) {
    const d: number = editDistance(ref, cand);
    if (d < bestDist) {
      bestDist = d;
      best = cand;
    }
  }
  const threshold: number = Math.max(2, Math.floor(ref.length * 0.4));
  return bestDist <= threshold ? best : "";
}

/**
 * A PATH or CONNECT only WIRES nodes that already exist — it never CREATES
 * them. An @alias used as a PATH/CONNECT source or target that is never
 * declared by a node command (TASK/SYSTEM/XGATEWAY/SUBPROCESS/LINKED/TIMER/
 * BOUNDARY) is a dangling reference that slips past BOTH existing guards:
 * `validateForwardReferences` only checks after:/before:/on: modifiers, and
 * `validateNodeConnectivity` only iterates DECLARED nodes — so a script full of
 * dangling edges "passes" structurally and then fails at execution with
 * "Source or Target node not found". The canonical trigger is an alias mismatch
 * (KOA-17362 "sikayet": `PATH @gw -> @kaliteBilgi` while the step was actually
 * built as `SYSTEM @kaliteBildirim`, and `PATH @kategoriGw -> @teknik` with the
 * three branch targets never declared as TASKs). Without this rule the agent
 * only ever sees the SECONDARY symptom (the separately-built node is
 * "isolated") and thrashes, never told the PATH target itself is undefined.
 * Flag every undeclared @ref and, when one is close to a real alias, suggest it.
 */
function validateConnectionRefsDeclared(
  nodes: Map<string, NodeEntry>,
  paths: PathCmd[],
  connects: ConnectCmd[],
  errors: ValidationError[]
): void {
  const declared: string[] = [...nodes.keys()];
  const declaredSet: Set<string> = new Set<string>(declared);

  const checkRef = (ref: string | undefined, role: string, line: number): void => {
    if (!ref || !ref.startsWith("@")) return; // literal BPMN IDs (Activity_x, …) are fine
    if (refIsStart(ref) || refIsEnd(ref)) return; // reserved Start/End, with or without @
    if (declaredSet.has(ref)) return;
    const suggestion: string = nearestAlias(ref, declared);
    const didYouMean: string = suggestion ? ` Did you mean "${suggestion}"?` : "";
    errors.push({
      line,
      rule: "undefined-connection-ref",
      message:
        `${role} "${ref}" is not a declared node. A PATH/CONNECT only WIRES existing nodes — it does NOT create them. ` +
        `Declare "${ref}" first with a node command (TASK/SYSTEM/XGATEWAY/SUBPROCESS/LINKED/TIMER), or fix the alias to match a node you already built.${didYouMean}`,
      severity: "error"
    });
  };

  for (const p of paths) {
    checkRef(p.sourceNodeId, "PATH source", p.line);
    checkRef(p.targetNodeId, "PATH target", p.line);
  }
  for (const c of connects) {
    checkRef(c.sourceNodeId, "CONNECT source", c.line);
    checkRef(c.targetNodeId, "CONNECT target", c.line);
  }
}

/**
 * A non-gateway activity (Task / System / Subprocess / Linked / Timer) may
 * have AT MOST ONE outgoing sequence flow — branching requires a Gateway
 * (the tool contract's rule #4: "Task/Step: EXACTLY 1 outgoing. Branch =
 * Gateway"). Kuika's runtime lint does NOT catch a multi-outgoing activity,
 * so without this a task silently ends up with two arrows. The canonical
 * trigger: a boundary-escalation target wired BOTH via `after:<hostTask>`
 * AND via the boundary's `CONNECT @boundary -> <target>`, leaving the host
 * task with a wrong second outgoing edge to the escalation step. Count
 * DISTINCT targets so a redundant duplicate edge (same target) isn't flagged.
 */
function validateSingleOutgoingNonGateway(
  nodes: Map<string, NodeEntry>,
  paths: PathCmd[],
  connects: ConnectCmd[],
  errors: ValidationError[]
): void {
  for (const [alias, node] of nodes) {
    if (node.kind === "GATEWAY" || node.kind === "BOUNDARY") continue;

    const targets = new Set<string>();
    // This node created with before:Y → alias → Y
    if (node.beforeRef) targets.add(node.beforeRef);
    // Another node created with after:alias → alias → that node
    for (const [otherAlias, other] of nodes) {
      if (otherAlias !== alias && other.afterRef === alias) targets.add(otherAlias);
    }
    // PATH / CONNECT with this node as source
    for (const p of paths) if (p.sourceNodeId === alias) targets.add(p.targetNodeId);
    for (const c of connects) if (c.sourceNodeId === alias) targets.add(c.targetNodeId);

    if (targets.size > 1) {
      errors.push({
        line: node.line,
        rule: "non-gateway-single-outgoing",
        message:
          `"${alias}" (${node.kind}) has ${targets.size} outgoing flows (to ${[...targets].join(", ")}). ` +
          `A non-gateway step may have only ONE outgoing — branching requires a Gateway. ` +
          `To branch on a condition, add an XGATEWAY after "${alias}" and PATH from the gateway. ` +
          `If a downstream step is reached ONLY via a boundary event, its incoming comes from the boundary's ` +
          `\`CONNECT @boundary -> <step>\` — do NOT also create that step with \`after:${alias}\`, which adds a wrong normal-flow edge from "${alias}".`,
        severity: "error"
      });
    }
  }
}

/**
 * KOA-17362 §7.6 Eksen 1 — task-screen field config must be applicable and sane.
 *
 * Three deterministic checks, all cheap and pre-execution:
 *  1. Every SCREEN_FIELD's step must be assigned by an ASSIGN_SCREEN in the SAME
 *     script. Field config is written when the managed screen is generated, so a
 *     lone SCREEN_FIELD is a no-op the author cannot see. (The executor also
 *     reports this at runtime; catching it here saves a whole round-trip.)
 *  2. Only a user TASK gets a managed screen — configuring a SYSTEM step, gateway
 *     or event is meaningless.
 *  3. An override set that hides EVERY business-object param the script declares
 *     would ship a blank approval screen. The backend does not guard this
 *     (`ValidateMetadataOutput` accepts a fieldless workflow form), so we do.
 *     Only checked when the script itself declares the BO params — in an edit
 *     script the form's real field set lives in the workflow, and the runtime
 *     merge has the authority there.
 */
function validateScreenFieldConfig(
  commands: DslCommand[],
  nodes: Map<string, NodeEntry>,
  errors: ValidationError[]
): void {
  const screenFields: ScreenFieldCmd[] = commands.filter(
    (c: DslCommand): c is ScreenFieldCmd => c.kind === "SCREEN_FIELD"
  );
  const screenSections: ScreenSectionCmd[] = commands.filter(
    (c: DslCommand): c is ScreenSectionCmd => c.kind === "SCREEN_SECTION"
  );
  const screenForms: ScreenFormCmd[] = commands.filter((c: DslCommand): c is ScreenFormCmd => c.kind === "SCREEN_FORM");
  if (screenFields.length === 0 && screenSections.length === 0 && screenForms.length === 0) return;

  const assignedRefs: Set<string> = new Set<string>();
  for (const cmd of commands) {
    if (cmd.kind !== "ASSIGN_SCREEN") continue;
    for (const ref of (cmd as AssignScreenCmd).stepIds) assignedRefs.add(ref);
  }

  const boParamCommands: ParamBoCmd[] = commands.filter((c: DslCommand): c is ParamBoCmd => c.kind === "PARAM_BO");
  const declaredBoParams: string[] = boParamCommands.map((c: ParamBoCmd): string => c.name);
  const declaredSectionTitles: Set<string> = new Set<string>(
    screenSections.map((c: ScreenSectionCmd): string => foldDslParamName(c.title))
  );
  // fold → declared data type, so an `element:` can be checked against the type the
  // SAME script declares. A param declared elsewhere (edit flow) is not checked here —
  // the runtime merge in `applyWorkflowFormOverrides` is the authority there.
  const declaredTypeByParam: Map<string, string> = new Map<string, string>(
    boParamCommands.map((c: ParamBoCmd): [string, string] => [foldDslParamName(c.name), c.dataType])
  );

  // param-fold → hidden?, per step ref. Only the LAST directive for a param counts,
  // mirroring the runtime merge (later line wins).
  const hiddenByStep: Map<string, Map<string, boolean>> = new Map<string, Map<string, boolean>>();

  // SCREEN_SECTION / SCREEN_FORM carry the same two preconditions as SCREEN_FIELD:
  // the step must be assigned by an ASSIGN_SCREEN in this script, and it must be a user
  // task (only those get a managed form).
  for (const cmd of [...screenSections, ...screenForms]) {
    const what: string = cmd.kind === "SCREEN_SECTION" ? `SCREEN_SECTION "${cmd.title}"` : "SCREEN_FORM";
    if (!assignedRefs.has(cmd.stepId)) {
      errors.push({
        line: cmd.line,
        rule: "screen-field-without-assign",
        message:
          `${what} targets "${cmd.stepId}" but this script never assigns a managed screen to it. ` +
          `Task-screen config is applied when the screen is generated — add ` +
          `\`ASSIGN_SCREEN <workflowId> ${cmd.stepId}\` to this SAME script (line order does not matter).`,
        severity: "error"
      });
      continue;
    }
    const sectionNode: NodeEntry | undefined = nodes.get(cmd.stepId);
    if (sectionNode && sectionNode.kind !== "TASK") {
      errors.push({
        line: cmd.line,
        rule: "screen-field-not-user-task",
        message:
          `${what} targets "${cmd.stepId}", which is a ${sectionNode.kind}, not a user task. ` +
          `Only a user task gets a managed screen with a form — a system step has no UI.`,
        severity: "error"
      });
    }
  }

  for (const cmd of screenFields) {
    if (!assignedRefs.has(cmd.stepId)) {
      errors.push({
        line: cmd.line,
        rule: "screen-field-without-assign",
        message:
          `SCREEN_FIELD targets "${cmd.stepId}" but this script never assigns a managed screen to it. ` +
          `Field config is applied when the screen is generated — add \`ASSIGN_SCREEN <workflowId> ${cmd.stepId}\` ` +
          `to this SAME script (line order does not matter).`,
        severity: "error"
      });
      continue;
    }

    const node: NodeEntry | undefined = nodes.get(cmd.stepId);
    if (node && node.kind !== "TASK") {
      errors.push({
        line: cmd.line,
        rule: "screen-field-not-user-task",
        message:
          `SCREEN_FIELD targets "${cmd.stepId}", which is a ${node.kind}, not a user task. ` +
          `Only a user task gets a managed screen with a form — a system step has no UI.`,
        severity: "error"
      });
      continue;
    }

    const routedSection: string = cmd.section?.trim() ?? "";
    if (routedSection.length > 0 && !declaredSectionTitles.has(foldDslParamName(routedSection))) {
      errors.push({
        line: cmd.line,
        rule: "screen-field-section-undeclared",
        message:
          `SCREEN_FIELD routes "${cmd.param}" into section "${routedSection}", which no SCREEN_SECTION declares. ` +
          `It will be created with the default 2-column layout and placed last — declare it with ` +
          `\`SCREEN_SECTION ${cmd.stepId} "${routedSection}" [columns:<1|2|4>]\` to control its order and layout.`,
        severity: "warning"
      });
    }

    const requestedElement: string = cmd.element?.trim() ?? "";
    if (requestedElement.length > 0) {
      const canonical: string | undefined = resolveElementTypeName(requestedElement);
      const declaredType: string | undefined = declaredTypeByParam.get(foldDslParamName(cmd.param));
      if (!canonical) {
        errors.push({
          line: cmd.line,
          rule: "screen-field-element-unknown",
          message:
            `SCREEN_FIELD element:"${requestedElement}" is not a form input widget` +
            (declaredType
              ? `. Valid widgets for ${cmd.param} (${declaredType}): ${describeElementTypeOptions(declaredType)}.`
              : "."),
          severity: "error"
        });
      } else if (declaredType && !isElementTypeValidForFieldType(canonical, declaredType)) {
        errors.push({
          line: cmd.line,
          rule: "screen-field-element-incompatible",
          message:
            `SCREEN_FIELD element:"${canonical}" is not available for "${cmd.param}", declared as ${declaredType}. ` +
            `Valid widgets for that type: ${describeElementTypeOptions(declaredType)}.`,
          severity: "error"
        });
      }
    }

    if (cmd.hidden === undefined) continue;
    const perStep: Map<string, boolean> = hiddenByStep.get(cmd.stepId) ?? new Map<string, boolean>();
    perStep.set(foldDslParamName(cmd.param), cmd.hidden);
    hiddenByStep.set(cmd.stepId, perStep);
  }

  if (declaredBoParams.length === 0) return;
  const declaredFolds: string[] = declaredBoParams.map((name: string): string => foldDslParamName(name));
  for (const [stepRef, perStep] of hiddenByStep) {
    const everyDeclaredHidden: boolean = declaredFolds.every((fold: string): boolean => perStep.get(fold) === true);
    if (!everyDeclaredHidden) continue;
    const firstLine: number = screenFields.find((c: ScreenFieldCmd): boolean => c.stepId === stepRef)?.line ?? 0;
    errors.push({
      line: firstLine,
      rule: "screen-field-all-hidden",
      message:
        `The SCREEN_FIELD config for "${stepRef}" hides EVERY business-object parameter (${declaredBoParams.join(", ")}), ` +
        `which leaves a blank task screen. Keep at least one field visible — an approver has to see what they are deciding on.`,
      severity: "error"
    });
  }
}

/**
 * KOA-18431 — task-screen composition, checked before anything is placed.
 *
 * Only what is DECIDABLE from the script belongs here. Two tempting rules are deliberately absent:
 *
 *   - "a composition line needs an ASSIGN_SCREEN in the same script" — it does not. Re-decorating a
 *     task screen that already exists is ordinary work, and demanding an assignment would either
 *     refuse it or force a heavy regeneration nobody asked for. The runtime says so precisely when
 *     the step really has no screen, naming the assignment as the remedy.
 *   - "an ORDER must name two siblings" — whether two components share a parent is a fact about the
 *     screen, which only exists at runtime.
 *
 * What IS decidable: an alias nothing declares can never resolve, a step that is not a user task
 * can never own a managed screen, and an engine-owned process action is never attachable here —
 * that last one by name alone, which is why it is caught before the round is spent (the runtime
 * refuses it too; see `workflow-dsl-screen-composition.ts` for why the law exists).
 */
function validateScreenComposition(
  commands: DslCommand[],
  nodes: Map<string, NodeEntry>,
  errors: ValidationError[]
): void {
  const composition: ScreenCompositionCommand[] = commands.filter(isScreenCompositionCommand);
  if (composition.length === 0) return;

  /** Every `#alias` this script brings into being, and the line that first declared it. */
  const declaredAt: Map<string, number> = new Map<string, number>();
  for (const command of composition) {
    const alias: string | undefined = compositionDeclaredAlias(command);
    if (alias === undefined) continue;
    const first: number | undefined = declaredAt.get(alias);
    if (first === undefined) {
      declaredAt.set(alias, command.line);
      continue;
    }
    errors.push({
      line: command.line,
      rule: "composition-duplicate-alias",
      message:
        `"${alias}" was already given to the component on line ${first}. Both components are still ` +
        `placed, but every later line naming "${alias}" now addresses THIS one — give them distinct names.`,
      severity: "error"
    });
  }

  for (const command of composition) {
    for (const [alias, role] of compositionReferencedAliases(command)) {
      if (declaredAt.has(alias)) continue;
      const suggestion: string = nearestAlias(alias, [...declaredAt.keys()]);
      const didYouMean: string = suggestion ? ` Did you mean "${suggestion}"?` : "";
      errors.push({
        line: command.line,
        rule: "composition-undefined-alias",
        message:
          `${role} "${alias}" is not a component or action this script names. A #alias exists only ` +
          `once a PLACE / CHILD / FIND / ACTION … AS line creates it; to address something an ` +
          `earlier script left on the screen, name it with \`FIND <stepRef> <ComponentName> AS ` +
          `<#alias>\` first.${didYouMean}`,
        severity: "error"
      });
    }
  }

  // KOA-18431 — A DATA HOST WITH NO CELLS SHOWS NOTHING.
  //
  // Live `02cda57d`: four `PLACE … Table` lines, a datasource bound to each, and not one `CHILD`.
  // The platform gives a fresh Table its default skeleton — one empty column — so the screen got a
  // blank strip that reads as "nothing was built". The data loads; there is simply nowhere for it
  // to appear. Same class as the filter traps: it ships silently and nothing refuses it. A warning
  // rather than an error, because the author may be about to add columns in a follow-up script.
  const boundHosts: Map<string, number> = new Map<string, number>();
  for (const command of composition) {
    if (command.kind === "BIND" && command.propertyName.toLowerCase() === "datasource") {
      if (!boundHosts.has(command.alias)) boundHosts.set(command.alias, command.line);
    }
    if (command.kind === "DATA" && command.propertyName.toLowerCase() === "datasource") {
      if (!boundHosts.has(command.alias)) boundHosts.set(command.alias, command.line);
    }
  }
  const hostsWithChildren: Set<string> = new Set<string>();
  for (const command of composition) {
    if (command.kind === "CHILD") hostsWithChildren.add(command.hostAlias);
    // A `FIND` against the host counts too: a script that names the column the platform already
    // built, instead of adding one, has still given the rows somewhere to appear.
    if (command.kind === "FIND" && command.host.kind === "alias") hostsWithChildren.add(command.host.alias);
  }
  for (const [alias, line] of boundHosts) {
    if (hostsWithChildren.has(alias)) continue;
    errors.push({
      line,
      rule: "composition-data-host-without-cells",
      message:
        `"${alias}" is given a datasource but no cell reads it — this script adds no ` +
        `\`CHILD ${alias} <Column> …\` to it. The platform renders a fresh data host with its ` +
        `default empty column, so the rows arrive and the screen shows a blank strip. For a Table, ` +
        `each column is \`CHILD ${alias} TableDefaultColumn AS <#col>\` (it lands in the table's ` +
        `row, which is where the catalog keeps columns) + \`SET <#col> title "<Header>"\` + ` +
        `\`FIND <#col> Label AS <#cell>\` + \`BIND <#cell> value row:<Field>\` — the value hangs ` +
        `on the Label INSIDE the column, not on the column.`,
      severity: "warning"
    });
  }

  // KOA-18431 — `pageInit` IS THE BODY'S, whatever component is named.
  //
  // The full "is this an event the host declares?" question needs the project's component catalog,
  // which this module deliberately does not have — the applier asks it, per line. But one case is
  // decidable from the script alone and is the one that shipped: live `02cda57d` hung a screen-LOAD
  // read on a Table. No component declares `pageInit`; the screen body does (`ROOT_SECTIONS["-1"]`).
  // Caught here so the round is not spent finding out.
  for (const command of composition) {
    if (command.kind !== "ACTION") continue;
    if (command.host.kind !== "alias") continue;
    if (command.eventName.toLowerCase() !== "pageinit") continue;
    errors.push({
      line: command.line,
      rule: "composition-page-init-on-component",
      message:
        `"pageInit" is the SCREEN BODY's event, not "${command.host.alias}"'s — no component ` +
        `declares it. Written on a component the action lands in the tree, shows in no property ` +
        `panel and never fires. A screen-load read hangs off the step: ` +
        `\`ACTION <stepRef> pageInit "${command.actionName}"\`.`,
      severity: "error"
    });
  }

  for (const command of composition) {
    if (command.kind !== "ACTION") continue;
    if (!isEngineProcessAction(command.actionName)) continue;
    errors.push({
      line: command.line,
      rule: "composition-engine-process-action",
      message:
        `"${command.actionName}" is one of the engine's own process actions and may not be attached ` +
        `to a task screen: the screen already carries the step its assignee is holding, the engine ` +
        `drives the instance, and its admin reads answer for every user rather than this one. ` +
        `Attach a datasource action of this application instead, or leave the control off.`,
      severity: "error"
    });
  }

  // KOA-18431 — A CHOICE LIST WITH NO CHOICES.
  //
  // `DATA #x options fixed` declares the list literal, and the platform's writer then creates it
  // EMPTY — that is what a fixed list is until somebody says what is in it. Live `c74f551e`: the
  // author wrote `fixed` plus the two column-name knobs and shipped a dropdown with nothing in it,
  // on both screens, and every line reported success. The entries are `OPTION` lines; without one
  // the control cannot offer anything.
  const fixedOptionTargets: Map<string, number> = new Map<string, number>();
  for (const command of composition) {
    if (command.kind !== "DATA") continue;
    if (command.actionName.trim().toLowerCase() !== "fixed") continue;
    const key: string = `${command.alias}\u0000${command.propertyName.toLowerCase()}`;
    if (!fixedOptionTargets.has(key)) fixedOptionTargets.set(key, command.line);
  }
  const optionTargets: Set<string> = new Set<string>();
  for (const command of composition) {
    if (command.kind !== "OPTION") continue;
    optionTargets.add(`${command.alias}\u0000${command.propertyName.toLowerCase()}`);
  }
  for (const [key, line] of fixedOptionTargets) {
    if (optionTargets.has(key)) continue;
    const [alias, propertyName] = key.split("\u0000");
    errors.push({
      line,
      rule: "composition-fixed-options-empty",
      message:
        `${alias}'s ${propertyName} is declared a FIXED list and this script names no entries for ` +
        `it, so the control ships with nothing to choose. Write one \`OPTION ${alias} ` +
        `${propertyName} "<value>" ["<label>"]\` line per choice, or point the property at an ` +
        `action instead (\`DATA ${alias} ${propertyName} "<ActionName>"\`).`,
      severity: "error"
    });
  }

  // KOA-18431 — a FIXED list's members are the platform's, not the author's.
  //
  // Its entries are `{key, text}` pairs and the writer names them so; a `DATAFIELD` pointing the
  // control at a column of some action's result overwrites that with names the list does not have,
  // and the dropdown renders empty even with every entry present. The live run wrote both lines
  // against a fixed list. Decidable here because the same script declared the list fixed.
  for (const command of composition) {
    if (command.kind !== "DATAFIELD") continue;
    const key: string = command.key.trim().toLowerCase();
    if (key !== "fieldtodisplay" && key !== "fieldtouseaskey") continue;
    if (!fixedOptionTargets.has(`${command.alias}\u0000${command.propertyName.toLowerCase()}`)) continue;
    errors.push({
      line: command.line,
      rule: "composition-fixed-options-column-name",
      message:
        `${command.alias}'s ${command.propertyName} is declared a FIXED list in this script, whose ` +
        `entries are value/label pairs — "${command.key}" names a column of an action's result and ` +
        `there is no action here. Write the choices with \`OPTION ${command.alias} ` +
        `${command.propertyName} "<value>" ["<label>"]\`, or point the property at an action with ` +
        `\`DATA ${command.alias} ${command.propertyName} "<ActionName>"\`.`,
      severity: "error"
    });
  }

  // KOA-18431 — AN INPUT FED FROM A CONTROL, ON A READ THAT RUNS BEFORE ANYONE TOUCHES IT.
  //
  // The screen body's own events fire while the screen is coming up. Every control on it still
  // holds its empty starting value then, so a read attached there and fed from one of them runs
  // once, with nothing. Live `c74f551e`: the query REQUIRED a category and was given the category
  // from the SelectBox beside it — "No data found", on every open, with no line refused. Decidable
  // from the script alone, so it is caught before the round is spent; the applier refuses it too.
  const screenLoadActionAliases: Set<string> = new Set<string>();
  for (const command of composition) {
    if (command.kind !== "ACTION" || command.actionAlias === undefined) continue;
    if (!isScreenLoadEvent(command.eventName)) continue;
    screenLoadActionAliases.add(command.actionAlias);
  }
  for (const command of composition) {
    if (command.kind !== "ARG") continue;
    if (command.source.kind !== "componentValue") continue;
    if (!screenLoadActionAliases.has(command.actionAlias)) continue;
    errors.push({
      line: command.line,
      rule: "composition-screen-load-input-from-control",
      message:
        `"${command.parameterName}" is fed from ${command.source.alias}, but ${command.actionAlias} runs while ` +
        `the screen is coming up — when that control is still empty, so the read runs once with no value. ` +
        `Feed it from something the screen already carries (\`process:<parameter>\`, a literal), or attach ` +
        `the read to the control's own event (\`ACTION ${command.source.alias} onChange "<ActionName>" AS ` +
        `${command.actionAlias}\`) so it runs when the value exists.`,
      severity: "error"
    });
  }

  for (const command of composition) {
    if (command.kind !== "PLACE") continue;
    const node: NodeEntry | undefined = nodes.get(command.stepId);
    if (node === undefined || node.kind === "TASK") continue;
    errors.push({
      line: command.line,
      rule: "composition-not-user-task",
      message:
        `PLACE targets "${command.stepId}", which is a ${node.kind}, not a user task. Only a user task ` +
        `gets a managed screen — a system step, a gateway and an event have no UI to compose.`,
      severity: "error"
    });
  }
}

/**
 * Fold a DSL-authored parameter name for comparison. Same convention as
 * `foldParamNameForMatch` in `workflow-form-metadata-overrides` (the runtime
 * authority) — kept local so the validator stays a pure, dependency-free module.
 */
function foldDslParamName(value: string): string {
  return (value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0131/g, "i")
    .replace(/\u0130/g, "i")
    .replace(/\u011f/g, "g")
    .replace(/\u011e/g, "g")
    .replace(/\u015f/g, "s")
    .replace(/\u015e/g, "s")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Warn if LINT or LAYOUT is missing — they should be the last commands. */
/**
 * KOA-17362 Adım 3 — Happy Path phase declarations must be usable.
 *
 * Two checks, both cheap and pre-execution:
 *  1. A duplicate `PHASE "<name>"` in the SAME script is rejected. The executor
 *     treats a re-declaration as an idempotent reuse (so a LATER script can
 *     `PHASE_ASSIGN` without re-declaring), but writing the same name twice in
 *     ONE script is always an authoring mistake: the author believes they created
 *     two distinct phases and the second one silently is the first, so the
 *     progress summary loses a stage.
 *  2. `PHASE_ASSIGN` must name a phase this script declares, UNLESS the script
 *     declares no phases at all — in that case the phase legitimately lives in
 *     the workflow already (the edit flow), and the executor resolves it there,
 *     erroring with the live list if it is missing. Checking the mixed case here
 *     catches the common typo one round-trip earlier.
 */
function validateHappyPathPhases(commands: DslCommand[], errors: ValidationError[]): void {
  const declarations: PhaseCmd[] = commands.filter((c: DslCommand): c is PhaseCmd => c.kind === "PHASE");
  const assignments: PhaseAssignCmd[] = commands.filter(
    (c: DslCommand): c is PhaseAssignCmd => c.kind === "PHASE_ASSIGN"
  );
  if (declarations.length === 0 && assignments.length === 0) return;

  const seen: Map<string, PhaseCmd> = new Map<string, PhaseCmd>();
  for (const decl of declarations) {
    const key: string = decl.name.trim().toLowerCase();
    const first: PhaseCmd | undefined = seen.get(key);
    if (first !== undefined) {
      errors.push({
        line: decl.line,
        rule: "phase-duplicate-name",
        message:
          `PHASE "${decl.name}" is already declared on line ${first.line}. Phases are addressed by name, ` +
          `so a second declaration reuses the first instead of creating a new stage — ` +
          `use a distinct name, or drop this line if the phase is already declared.`,
        severity: "error"
      });
      continue;
    }
    seen.set(key, decl);
  }

  if (declarations.length === 0) return; // edit flow — the phase lives in the workflow
  const declaredNames: string = declarations.map((d: PhaseCmd): string => `"${d.name}"`).join(", ");
  for (const assign of assignments) {
    if (seen.has(assign.phaseName.trim().toLowerCase())) continue;
    errors.push({
      line: assign.line,
      rule: "phase-assign-unknown-phase",
      message:
        `PHASE_ASSIGN targets phase "${assign.phaseName}" but this script does not declare it. ` +
        `Declared here: ${declaredNames}. Add \`PHASE "${assign.phaseName}"\` or fix the name.`,
      severity: "error"
    });
  }
}

// ── Structural-defect disposition (KOA-18136 §33) ──────────────────────────

/**
 * Rules whose OFFENDING LINE must not be executed: building it would put a
 * wrong element on the diagram (an edge pointing into Start, a conditional
 * path out of a Task, a duplicate phase). Skipping the single line is strictly
 * better than both alternatives — building garbage, or throwing the whole
 * script away.
 *
 * Everything NOT listed here is a GRAPH-QUALITY defect: the element itself is
 * legitimate and only its wiring is incomplete (a node with no incoming edge,
 * a gateway missing its DEFAULT path). Those are built as written and reported
 * back, because a node that exists with one missing arrow is one line away
 * from correct, while a node that was never created costs a full rewrite.
 */
export const SKIP_LINE_RULES: ReadonlySet<string> = new Set<string>([
  "edge-into-start",
  "edge-out-of-end",
  "path-source-must-be-gateway",
  "path-duplicate-target",
  "parallel-no-default",
  "parallel-no-conditions",
  "phase-duplicate-name",
  "phase-assign-unknown-phase",
  "screen-field-without-assign",
  "screen-field-not-user-task",
  "screen-field-section-undeclared",
  "screen-field-element-unknown",
  "screen-field-element-incompatible",
  "screen-field-all-hidden",
  "composition-undefined-alias",
  "composition-not-user-task",
  "composition-engine-process-action",
  "composition-page-init-on-component",
  "composition-fixed-options-empty",
  "composition-fixed-options-column-name",
  "composition-screen-load-input-from-control"
]);

export interface StructuralPartition {
  /** Errors whose line must be dropped before execution. */
  skipped: ValidationError[];
  /** Errors that are reported but do NOT stop the script from running. */
  reported: ValidationError[];
  /** Line numbers to filter out of the command list. */
  skipLines: number[];
}

/**
 * Split fatal validation errors into "drop this line" and "build it anyway and
 * tell the author".
 *
 * KOA-18136 §33 — until now a single fatal error threw the ENTIRE script away
 * without executing one command. Measured on the live run (session 947ce0d8):
 * a 141-line process with one unconnected system step was rejected in 23ms and
 * the process stayed empty; the builder then read the empty graph's clean lint
 * as success and ended the turn. The 140 sound lines were free to run.
 */
export function partitionStructuralErrors(errors: readonly ValidationError[]): StructuralPartition {
  const skipped: ValidationError[] = [];
  const reported: ValidationError[] = [];
  for (const error of errors) {
    if (error.severity !== "error") continue;
    // A rule with no line to point at (line 0 = script-level) can never be
    // skipped surgically — report it.
    if (SKIP_LINE_RULES.has(error.rule) && error.line > 0) skipped.push(error);
    else reported.push(error);
  }
  const skipLines: number[] = Array.from(new Set<number>(skipped.map((e: ValidationError): number => e.line))).sort(
    (a: number, b: number): number => a - b
  );
  return { skipped, reported, skipLines };
}

function validateMissingLintLayout(hasLint: boolean, hasLayout: boolean, errors: ValidationError[]): void {
  if (!hasLint) {
    errors.push({
      line: 0,
      rule: "missing-lint",
      message: "Script should end with LINT and LAYOUT commands. LINT is missing.",
      severity: "warning"
    });
  }
  if (!hasLayout) {
    errors.push({
      line: 0,
      rule: "missing-layout",
      message: "Script should end with LAYOUT command. LAYOUT is missing.",
      severity: "warning"
    });
  }
}
